<template>
  <div id="view">
    <historyTag :historyArr="historyArr"></historyTag>
    <div id="mainBox" class="pageMainBox">
      <div class="sysSettingShell">
        <aside class="sysSettingAside">
          <div class="asideHead">配置分组</div>
          <ul class="groupNav">
            <li
              v-for="g in groupMenuItems"
              :key="g.index"
              :class="{ active: selectedGroupKey === g.index }"
              @click="selectedGroupKey = g.index"
            >
              {{ g.label }}
            </li>
          </ul>
        </aside>

        <section class="sysSettingPane">
          <div v-if="showPickupCourierPanel" class="pickupCourierPanel">
            <div class="pickupPanelHead">
              <div>
                <h3 class="pickupPanelTitle">预约寄件 · 快递开放开关</h3>
                <p class="pickupPanelSub">控制小程序「标准寄」可选快递与「顺丰尊享寄」入口；保存后约 1 分钟内同步到小程序。</p>
              </div>
              <div class="pickupPanelStats">
                <span class="statChip">标准寄已开 {{ pickupEnabledStandardCount }} / {{ pickupCourierDraft.standardCouriers.length }}</span>
                <span class="statChip" :class="{ 'statChip--on': pickupCourierDraft.premiumEnabled }">
                  尊享寄 {{ pickupCourierDraft.premiumEnabled ? "已开放" : "已关闭" }}
                </span>
              </div>
            </div>

            <div class="pickupSection">
              <div class="pickupSectionHead">
                <span class="pickupSectionTitle">标准寄（六选一）</span>
                <div class="pickupQuickBtns">
                  <el-button size="mini" @click="applyPickupPreset('all')">全部开启</el-button>
                  <el-button size="mini" @click="applyPickupPreset('jdOnly')">仅京东</el-button>
                  <el-button size="mini" @click="applyPickupPreset('jdSf')">京东 + 顺丰尊享</el-button>
                  <el-button size="mini" @click="applyPickupPreset('none')">全部关闭</el-button>
                </div>
              </div>
              <div class="courierGrid">
                <div
                  v-for="item in pickupCourierDraft.standardCouriers"
                  :key="item.code"
                  class="courierCard"
                  :class="{ 'courierCard--on': item.enabled }"
                >
                  <div class="courierCardTop">
                    <span class="courierName">{{ item.name }}</span>
                    <el-switch
                      v-model="item.enabled"
                      active-color="#13ce66"
                      inactive-color="#dcdfe6"
                    />
                  </div>
                  <code class="courierCode">{{ item.code }}</code>
                  <span class="courierStatus">{{ item.enabled ? "小程序可见" : "已隐藏" }}</span>
                </div>
              </div>
              <p v-if="pickupEnabledStandardCount === 0" class="pickupWarn">至少开启一家标准寄快递，否则用户无法选择标准寄。</p>
            </div>

            <div class="pickupSection pickupSection--premium">
              <div class="pickupSectionHead">
                <span class="pickupSectionTitle">顺丰尊享寄</span>
              </div>
              <div class="premiumRow">
                <div class="premiumInfo">
                  <span class="premiumName">顺丰速运 · 标快上门</span>
                  <code class="courierCode">shunfeng</code>
                </div>
                <el-switch
                  v-model="pickupCourierDraft.premiumEnabled"
                  active-color="#13ce66"
                  inactive-color="#dcdfe6"
                />
                <span class="switchTxt">{{ pickupCourierDraft.premiumEnabled ? "小程序展示「顺丰尊享寄」" : "隐藏尊享寄入口" }}</span>
              </div>
            </div>

            <div class="pickupPanelActions">
              <el-button type="primary" :loading="pickupCourierDraft.saving" @click="savePickupCourierConfig">保存预约寄件配置</el-button>
            </div>
          </div>

          <div v-if="displayRows.length" class="settingFormList">
            <div v-for="row in displayRows" :key="row.id" class="settingBlock">
              <div class="settingHead">
                <div class="settingTitleRow">
                  <span class="settingTitle">{{ displayTitle(row) }}</span>
                </div>
                <code class="settingKeyHint">{{ row.settingKey }}</code>
              </div>

              <div class="settingBody">
                <template v-if="isConsignPlatformCommissionKey(row.settingKey)">
                  <div class="discountRow">
                    <span class="switchLabel">平台抽成</span>
                    <el-input
                      v-model="row.settingValue"
                      placeholder="0.05"
                      class="discountInput"
                    />
                    <span class="discountHint">相对挂牌价，如 0.05 = 5%，货主结算 95%</span>
                  </div>
                  <p class="fieldTip">
                    无买家补贴时平台拿满额抽成；补贴活动开启后（如 96 折），平台实际拿买家实付 − 货主结算（5% 让利 4% 后约剩 1%）。
                  </p>
                  <div class="rowActions">
                    <el-button type="primary" size="small" :loading="!!row._saving" @click="saveRow(row)">保存抽成</el-button>
                  </div>
                </template>

                <template v-else-if="isConsignPromoKey(row.settingKey)">
                  <div class="switchCell">
                    <span class="switchLabel">补贴活动</span>
                    <el-switch
                      :value="row.status === 0"
                      :disabled="!!row._statusSaving"
                      @change="(val) => onConsignPromoStatusToggle(row, val)"
                    />
                    <span class="switchTxt">{{ row.status === 0 ? "已启用（使用 consign_listing_price + 折后 price）" : "已停用（仅以 price 为准）" }}</span>
                  </div>
                  <p class="fieldTip">关闭活动后，新改价/上架不再写入 consign_listing_price；建议同步执行批量恢复 SQL（见迁移脚本说明）。</p>
                  <div class="discountRow">
                    <span class="switchLabel">买家折扣</span>
                    <el-input
                      v-model="row.settingValue"
                      placeholder="0.96"
                      class="discountInput"
                      :disabled="row.status !== 0"
                    />
                    <span class="discountHint">相对货主售卖价，如 0.96 = 96 折</span>
                  </div>
                  <div class="rowActions">
                    <el-button
                      type="primary"
                      size="small"
                      :loading="!!row._saving"
                      :disabled="row.status !== 0"
                      @click="saveRow(row)"
                    >保存折扣</el-button>
                  </div>
                </template>

                <template v-else-if="isBooleanSwitchKey(row.settingKey)">
                  <div class="switchCell">
                    <el-switch
                      :value="toggleValueNormalized(row.settingValue)"
                      active-value="1"
                      inactive-value="0"
                      active-color="#13ce66"
                      :disabled="!!row._switchSaving"
                      @change="(val) => onTogglePersist(row, val)"
                    />
                    <span class="switchTxt">{{ toggleValueNormalized(row.settingValue) === "1" ? switchOnText(row.settingKey) : switchOffText(row.settingKey) }}</span>
                  </div>
                  <p class="fieldTip">开启后仅首页列表、搜索列表、商品详情接口返回 CDN 地址；订单/聊天等仍走源站。CDN 过期请关闭。</p>
                </template>

                <template v-else-if="(row.valueType || '').toUpperCase() === 'IMAGE'">
                  <div class="uploadHint">二维码 / 图片（上传后填入链接）</div>
                  <div class="uploadBox">
                    <elUpload @getImg="(url) => onRowImg(row, url)" :image="row.settingValue || ''"></elUpload>
                  </div>
                  <el-input v-model="row.settingValue" type="textarea" :rows="2" placeholder="图片 URL" />
                  <div class="rowActions">
                    <el-button type="primary" size="small" :loading="!!row._saving" @click="saveRow(row)">保存</el-button>
                  </div>
                </template>

                <template v-else>
                  <el-input v-model="row.settingValue" type="textarea" :rows="4" placeholder="配置值" class="valueInput" />
                  <div class="rowActions">
                    <el-button type="primary" size="small" :loading="!!row._saving" @click="saveRow(row)">保存</el-button>
                  </div>
                </template>

              </div>
            </div>
          </div>

          <el-empty v-else-if="!showPickupCourierPanel" description="当前分组暂无配置项" />
        </section>
      </div>
    </div>
  </div>
</template>

<script>
import elUpload from "@/components/elUpload";

/** 配置键对应布尔开关（存库仍为 1/0 字符串） */
const BOOLEAN_SWITCH_META = {
  "secondhand.content_audit.enabled": {
    on: "已开启审核",
    off: "已关闭审核",
  },
  "app.home_auction_entry.enabled": {
    on: "已展示入口",
    off: "已隐藏入口",
  },
  "fragrance.swap.entry.enabled": {
    on: "已展示香水置换入口",
    off: "已隐藏香水置换入口",
  },
  "image.cdn.rewrite.enabled": {
    on: "首页/搜索/详情接口返回 CDN",
    off: "全部接口返回源站（CDN 过期时用）",
  },
};

const CONSIGN_PROMO_KEY = "consign.promo.buyer_discount_rate";
const CONSIGN_PLATFORM_COMMISSION_KEY = "consign.platform_commission_rate";

const PICKUP_COURIER_LIST_KEY = "pickup.standard_courier_list";
const PICKUP_PREMIUM_ENABLED_KEY = "pickup.premium_courier_enabled";

/** 标准寄固定六家（与后端 PickupCourierConfigService 一致） */
const PICKUP_COURIER_CATALOG = [
  { code: "yuantong", name: "圆通" },
  { code: "jd", name: "京东" },
  { code: "debangkuaidi", name: "德邦快递" },
  { code: "zhongtong", name: "中通" },
  { code: "shentong", name: "申通" },
  { code: "yunda", name: "韵达" },
];

const MODULE_LABEL = {
  app: "小程序",
  jury: "陪审 / 法官",
  secondhand: "闲置",
  consignment: "官方寄售",
  pickup: "预约寄件",
  image: "图片 / CDN",
  address: "收货地址",
};

const GROUP_UNGROUPED = "__ungrouped__";

export default {
  components: { elUpload },
  data() {
    return {
      historyArr: ["系统管理", "配置管理"],
      allRows: [],
      selectedGroupKey: null,
      pickupCourierDraft: {
        standardCouriers: PICKUP_COURIER_CATALOG.map((c) => ({ ...c, enabled: true })),
        premiumEnabled: true,
        listRow: null,
        premiumRow: null,
        saving: false,
      },
    };
  },
  computed: {
    showPickupCourierPanel() {
      return this.selectedGroupKey === "pickup" && !!this.pickupCourierDraft.listRow;
    },
    pickupEnabledStandardCount() {
      const list = this.pickupCourierDraft.standardCouriers || [];
      return list.filter((c) => c.enabled).length;
    },
    /** 左侧分组：启用项 + 官方寄售补贴（停用后仍需可见以便重新开启） */
    groupMenuItems() {
      const keys = new Set();
      this.allRows.forEach((r) => {
        if (r.status === 1 && !this.isConsignPromoKey(r.settingKey)) return;
        const m = (r.module || "").trim();
        keys.add(m ? m : GROUP_UNGROUPED);
      });
      const rest = [...keys].filter((k) => k !== GROUP_UNGROUPED);
      rest.sort((a, b) => {
        const oa = Object.keys(MODULE_LABEL).indexOf(a);
        const ob = Object.keys(MODULE_LABEL).indexOf(b);
        if (oa !== -1 || ob !== -1) {
          if (oa === -1) return 1;
          if (ob === -1) return -1;
          return oa - ob;
        }
        return a.localeCompare(b, "zh");
      });
      const items = [];
      rest.forEach((k) => {
        items.push({ index: k, label: this.groupDisplayLabel(k) });
      });
      if (keys.has(GROUP_UNGROUPED)) {
        items.push({ index: GROUP_UNGROUPED, label: "未分组" });
      }
      return items;
    },
    filteredRows() {
      let rows = this.allRows.slice();
      if (this.selectedGroupKey == null || this.selectedGroupKey === "") {
        return [];
      }
      if (this.selectedGroupKey === GROUP_UNGROUPED) {
        rows = rows.filter((r) => !(r.module && String(r.module).trim()));
      } else {
        rows = rows.filter((r) => (r.module || "").trim() === this.selectedGroupKey);
      }
      rows = rows.filter((r) => r.status !== 1 || this.isConsignPromoKey(r.settingKey));
      return rows;
    },
    displayRows() {
      return this.filteredRows
        .slice()
        .filter((r) => !this.isPickupCourierConfigKey(r.settingKey))
        .sort((a, b) => String(a.settingKey || "").localeCompare(String(b.settingKey || "")));
    },
  },
  mounted() {
    this.loadList();
  },
  methods: {
    groupDisplayLabel(moduleKey) {
      if (moduleKey === GROUP_UNGROUPED) return "未分组";
      return MODULE_LABEL[moduleKey] ? `${MODULE_LABEL[moduleKey]}（${moduleKey}）` : moduleKey;
    },
    displayTitle(row) {
      const rmk = (row.remark || "").trim();
      if (rmk) return rmk;
      return row.settingKey || "（无键名）";
    },
    isBooleanSwitchKey(key) {
      const k = String(key || "").trim();
      if (!k) return false;
      if (Object.prototype.hasOwnProperty.call(BOOLEAN_SWITCH_META, k)) return true;
      // 约定：以 .enabled 结尾的配置统一按开关展示（1/0）
      return /\.enabled$/i.test(k);
    },
    isConsignPromoKey(key) {
      return String(key || "").trim() === CONSIGN_PROMO_KEY;
    },
    isConsignPlatformCommissionKey(key) {
      return String(key || "").trim() === CONSIGN_PLATFORM_COMMISSION_KEY;
    },
    isPickupCourierConfigKey(key) {
      const k = String(key || "").trim();
      return k === PICKUP_COURIER_LIST_KEY || k === PICKUP_PREMIUM_ENABLED_KEY;
    },
    syncPickupCourierDraftFromRows() {
      const listRow = this.allRows.find((r) => String(r.settingKey || "").trim() === PICKUP_COURIER_LIST_KEY);
      const premiumRow = this.allRows.find((r) => String(r.settingKey || "").trim() === PICKUP_PREMIUM_ENABLED_KEY);
      this.pickupCourierDraft.listRow = listRow || null;
      this.pickupCourierDraft.premiumRow = premiumRow || null;
      this.pickupCourierDraft.standardCouriers = this.parsePickupCourierList(
        listRow && listRow.settingValue
      );
      this.pickupCourierDraft.premiumEnabled = this.parsePremiumEnabled(
        premiumRow && premiumRow.settingValue
      );
    },
    parsePremiumEnabled(raw) {
      if (raw == null || String(raw).trim() === "") return true;
      const v = String(raw).trim().toLowerCase();
      return !["0", "false", "off", "no"].includes(v);
    },
    parsePickupCourierList(jsonStr) {
      const merged = PICKUP_COURIER_CATALOG.map((c) => ({ ...c, enabled: true }));
      if (!jsonStr || !String(jsonStr).trim()) {
        return merged;
      }
      try {
        const arr = JSON.parse(String(jsonStr).trim());
        if (!Array.isArray(arr) || !arr.length) {
          return merged;
        }
        const hasEnabledField = arr.some((o) => o && typeof o === "object" && "enabled" in o);
        merged.forEach((c) => {
          if (hasEnabledField) c.enabled = false;
        });
        arr.forEach((o) => {
          if (!o || !o.code) return;
          const code = String(o.code).trim().toLowerCase();
          const hit = merged.find((c) => c.code === code);
          if (!hit) return;
          if (o.name) hit.name = String(o.name).trim();
          hit.enabled = hasEnabledField ? this.parseBoolField(o.enabled, true) : true;
        });
        return merged;
      } catch (e) {
        return merged;
      }
    },
    parseBoolField(raw, defaultVal) {
      if (raw === true || raw === 1 || raw === "1") return true;
      if (raw === false || raw === 0 || raw === "0") return false;
      if (raw == null || String(raw).trim() === "") return defaultVal;
      const v = String(raw).trim().toLowerCase();
      if (["true", "yes", "on"].includes(v)) return true;
      if (["false", "no", "off"].includes(v)) return false;
      return defaultVal;
    },
    buildPickupCourierJson() {
      return JSON.stringify(
        (this.pickupCourierDraft.standardCouriers || []).map((c) => ({
          code: c.code,
          name: c.name,
          enabled: !!c.enabled,
        }))
      );
    },
    applyPickupPreset(preset) {
      const list = this.pickupCourierDraft.standardCouriers || [];
      if (preset === "all") {
        list.forEach((c) => { c.enabled = true; });
        this.pickupCourierDraft.premiumEnabled = true;
      } else if (preset === "none") {
        list.forEach((c) => { c.enabled = false; });
        this.pickupCourierDraft.premiumEnabled = false;
      } else if (preset === "jdOnly") {
        list.forEach((c) => { c.enabled = c.code === "jd"; });
        this.pickupCourierDraft.premiumEnabled = false;
      } else if (preset === "jdSf") {
        list.forEach((c) => { c.enabled = c.code === "jd"; });
        this.pickupCourierDraft.premiumEnabled = true;
      }
    },
    savePickupCourierConfig() {
      const listRow = this.pickupCourierDraft.listRow;
      if (!listRow || !listRow.id) {
        this.$message.error("未找到 pickup.standard_courier_list 配置项，请先执行数据库迁移");
        return;
      }
      if (this.pickupEnabledStandardCount === 0 && !this.pickupCourierDraft.premiumEnabled) {
        this.$message.warning("标准寄与尊享寄至少需开放一项");
        return;
      }
      this.pickupCourierDraft.saving = true;
      const json = this.buildPickupCourierJson();
      const premiumVal = this.pickupCourierDraft.premiumEnabled ? "true" : "false";
      const tasks = [
        this.persistSysSettingRow(listRow, { settingValue: json }),
      ];
      const premiumRow = this.pickupCourierDraft.premiumRow;
      if (premiumRow && premiumRow.id) {
        tasks.push(this.persistSysSettingRow(premiumRow, { settingValue: premiumVal }));
      }
      Promise.all(tasks)
        .then((results) => {
          const failed = results.find((r) => !r.ok);
          if (failed) {
            this.$message.error(failed.msg || "保存失败");
            return;
          }
          listRow.settingValue = json;
          if (premiumRow) premiumRow.settingValue = premiumVal;
          this.$message.success("预约寄件快递开关已保存");
        })
        .catch(() => {
          this.$message.error("网络错误");
        })
        .finally(() => {
          this.pickupCourierDraft.saving = false;
        });
    },
    persistSysSettingRow(row, patch) {
      return this.$https(this.$api.api.upSysSetting, {
        id: row.id,
        settingValue: patch.settingValue != null ? patch.settingValue : row.settingValue,
        valueType: row.valueType || "STRING",
        module: row.module || "",
        remark: row.remark || "",
        status: row.status != null ? row.status : 0,
      }, "POST").then((res) => {
        if (res.data.code === 200) {
          return { ok: true };
        }
        return { ok: false, msg: res.data.msg || "保存失败" };
      });
    },
    toggleValueNormalized(v) {
      if (v === null || v === undefined || String(v).trim() === "") return "1";
      const s = String(v).trim().toLowerCase();
      if (["0", "false", "off", "no", "disabled", "关", "关闭", "否"].indexOf(s) !== -1) return "0";
      return "1";
    },
    switchOnText(key) {
      const m = BOOLEAN_SWITCH_META[String(key || "").trim()];
      return (m && m.on) || "开";
    },
    switchOffText(key) {
      const m = BOOLEAN_SWITCH_META[String(key || "").trim()];
      return (m && m.off) || "关";
    },
    onRowImg(row, url) {
      this.$set(row, "settingValue", url);
    },
    onConsignPromoStatusToggle(row, enabled) {
      const nextStatus = enabled ? 0 : 1;
      const prevStatus = row.status;
      row.status = nextStatus;
      this.$set(row, "_statusSaving", true);
      this.$https(this.$api.api.upSysSetting, {
        id: row.id,
        settingValue: row.settingValue,
        valueType: row.valueType || "STRING",
        module: row.module || "consignment",
        remark: row.remark || "",
        status: nextStatus,
      }, "POST")
        .then((res) => {
          if (res.data.code === 200) {
            this.$message.success(enabled ? "补贴活动已启用" : "补贴活动已停用");
          } else {
            row.status = prevStatus;
            this.$message.error(res.data.msg || "更新失败");
          }
        })
        .catch(() => {
          row.status = prevStatus;
          this.$message.error("网络错误");
        })
        .finally(() => {
          this.$set(row, "_statusSaving", false);
        });
    },
    onTogglePersist(row, val) {
      const next = val === "0" || val === 0 ? "0" : "1";
      const prev = row.settingValue;
      row.settingValue = next;
      this.$set(row, "_switchSaving", true);
      this.$https(this.$api.api.upSysSetting, {
        id: row.id,
        settingValue: next,
        valueType: row.valueType || "STRING",
        module: row.module || "",
        remark: row.remark || "",
        status: 0,
      }, "POST")
        .then((res) => {
          if (res.data.code === 200) {
            this.$message.success("已更新");
          } else {
            row.settingValue = prev;
            this.$message.error(res.data.msg || "更新失败");
          }
        })
        .catch(() => {
          row.settingValue = prev;
          this.$message.error("网络错误");
        })
        .finally(() => {
          this.$set(row, "_switchSaving", false);
        });
    },
    saveRow(row) {
      this.$set(row, "_saving", true);
      this.$https(this.$api.api.upSysSetting, {
        id: row.id,
        settingValue: row.settingValue,
        valueType: row.valueType || "STRING",
        module: row.module || "",
        remark: row.remark || "",
        status: row.status != null ? row.status : 0,
      }, "POST")
        .then((res) => {
          if (res.data.code === 200) {
            this.$message.success("保存成功");
          } else {
            this.$message.error(res.data.msg || "保存失败");
          }
        })
        .catch(() => {
          this.$message.error("网络错误");
        })
        .finally(() => {
          this.$set(row, "_saving", false);
        });
    },
    ensureSelectedGroup() {
      const items = this.groupMenuItems;
      if (!items.length) {
        this.selectedGroupKey = null;
        return;
      }
      const hit = items.some((x) => x.index === this.selectedGroupKey);
      if (!hit) {
        this.selectedGroupKey = items[0].index;
      }
    },
    loadList() {
      const obj = { page: 1, size: 500 };
      this.$https(this.$api.api.selSysSetting, obj, "POST").then((res) => {
        if (res.data.code === 200) {
          this.allRows = res.data.data || [];
          this.syncPickupCourierDraftFromRows();
          this.$nextTick(() => {
            this.ensureSelectedGroup();
          });
        } else {
          this.$message.error(res.data.msg || "加载失败");
        }
      });
    },
  },
};
</script>

<style scoped>
.pageMainBox {
  background: transparent;
}

.sysSettingShell {
  display: flex;
  align-items: stretch;
  min-height: 520px;
  background: #fff;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.sysSettingAside {
  width: 216px;
  flex-shrink: 0;
  border-right: 1px solid #ebeef5;
  background: #fafbfc;
}

.asideHead {
  padding: 14px 16px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  border-bottom: 1px solid #ebeef5;
  background: #f5f7fa;
}

.groupNav {
  list-style: none;
  margin: 0;
  padding: 8px 0;
}

.groupNav li {
  padding: 11px 16px;
  cursor: pointer;
  font-size: 13px;
  color: #606266;
  border-left: 3px solid transparent;
  transition: background 0.15s ease, color 0.15s ease;
}

.groupNav li:hover {
  background: #ecf5ff;
  color: #409eff;
}

.groupNav li.active {
  background: #ecf5ff;
  color: #409eff;
  font-weight: 500;
  border-left-color: #409eff;
}

.sysSettingPane {
  flex: 1;
  min-width: 0;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.settingFormList {
  max-width: 720px;
}

.settingBlock {
  padding: 16px 0;
  border-bottom: 1px solid #ebeef5;
}

.settingBlock:last-child {
  border-bottom: none;
}

.settingHead {
  margin-bottom: 10px;
}

.settingTitleRow {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.settingTitle {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.settingKeyHint {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  word-break: break-all;
}

.settingBody {
  padding-left: 2px;
}

.valueInput {
  max-width: 640px;
}

.rowActions {
  margin-top: 12px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.fieldTip {
  margin: 8px 0 0;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.uploadHint {
  font-size: 12px;
  color: #606266;
  margin-bottom: 6px;
}

.uploadBox {
  width: 200px;
  height: 120px;
  margin-bottom: 10px;
}

.switchCell {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}

.switchTxt {
  font-size: 13px;
  color: #606266;
}

.switchLabel {
  font-size: 13px;
  color: #303133;
  min-width: 72px;
}

.discountRow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

.discountInput {
  max-width: 120px;
}

.discountHint {
  font-size: 12px;
  color: #909399;
}

.pickupCourierPanel {
  max-width: 880px;
  margin-bottom: 20px;
  padding: 20px;
  background: #f8fafc;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
}

.pickupPanelHead {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.pickupPanelTitle {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.pickupPanelSub {
  margin: 0;
  font-size: 13px;
  color: #909399;
  line-height: 1.5;
}

.pickupPanelStats {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.statChip {
  display: inline-block;
  padding: 4px 10px;
  font-size: 12px;
  color: #606266;
  background: #fff;
  border: 1px solid #dcdfe6;
  border-radius: 999px;
}

.statChip--on {
  color: #067647;
  border-color: #b7eb8f;
  background: #f6ffed;
}

.pickupSection {
  margin-bottom: 18px;
  padding: 16px;
  background: #fff;
  border-radius: 6px;
  border: 1px solid #ebeef5;
}

.pickupSection--premium {
  margin-bottom: 0;
}

.pickupSectionHead {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 14px;
}

.pickupSectionTitle {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.pickupQuickBtns {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.courierGrid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(148px, 1fr));
  gap: 12px;
}

.courierCard {
  padding: 12px;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  background: #fafafa;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.courierCard--on {
  border-color: #b7eb8f;
  background: #f6ffed;
}

.courierCardTop {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}

.courierName {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.courierCode {
  display: block;
  font-size: 11px;
  color: #909399;
  margin-bottom: 6px;
}

.courierStatus {
  font-size: 12px;
  color: #909399;
}

.courierCard--on .courierStatus {
  color: #389e0d;
}

.premiumRow {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.premiumInfo {
  min-width: 160px;
}

.premiumName {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  margin-bottom: 4px;
}

.pickupWarn {
  margin: 12px 0 0;
  font-size: 12px;
  color: #e6a23c;
}

.pickupPanelActions {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px dashed #e4e7ed;
}
</style>



// WEBPACK FOOTER //
// src/pages/sysSetting/index.vue